import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Lunar } from 'lunar-typescript';
import type { Trigram, YaoPosition, HexagramResult } from '../types/iching';
import { 
  calculateMainHexagram, 
  calculateChangedHexagram, 
  calculateInterlinkedHexagrams 
} from '../utils/hexagramCalculator';
import { getHexagramData } from '../data/hexagrams';
import { logger } from '../utils/clientLogger';
import * as divinationApi from '../api/divination';
import { asyncQueue } from '../utils/asyncOperations';

interface DivinationState {
  currentResult: HexagramResult | null;
  savedResults: HexagramResult[];
  upperTrigram: Trigram | '';
  lowerTrigram: Trigram | '';
  movingYao: YaoPosition | '';
  setSelections: (upper: Trigram | '', lower: Trigram | '', yao: YaoPosition | '') => void;
  performManualDivination: (upperTrigram: Trigram, lowerTrigram: Trigram, movingYao: YaoPosition) => void;
  performTimeDivination: () => void;
  saveCurrentResult: () => Promise<boolean>;
  loadSavedResult: (result: HexagramResult) => void;
  deleteSavedResult: (timestamp: string) => Promise<void>;
  syncWithServer: () => Promise<void>;
  reset: () => void;
}

export const useDivinationStore = create<DivinationState>()(
  persist(
    (set, get) => ({
      currentResult: null,
      savedResults: [],
      upperTrigram: '',
      lowerTrigram: '',
      movingYao: '',

      setSelections: (upper, lower, yao) => {
        logger.info('Setting divination selections', { upper, lower, yao });
        set({ upperTrigram: upper, lowerTrigram: lower, movingYao: yao });
      },

      performManualDivination: (upperTrigram, lowerTrigram, movingYao) => {
        logger.info('Performing manual divination', { upperTrigram, lowerTrigram, movingYao });
        
        const mainHexagram = calculateMainHexagram(upperTrigram, lowerTrigram);
        const changedHexagram = calculateChangedHexagram(mainHexagram, movingYao);
        const { useHexagram, bodyHexagram } = calculateInterlinkedHexagrams(mainHexagram, movingYao);
        
        const hexagramData = getHexagramData(mainHexagram);
        
        const result: HexagramResult = {
          mainHexagram,
          changedHexagram,
          useHexagram,
          bodyHexagram,
          mainText: hexagramData.text,
          yaoTexts: hexagramData.yaoTexts,
          movingYao: ['初爻', '二爻', '三爻', '四爻', '五爻', '上爻'].indexOf(movingYao),
          timestamp: new Date().toISOString(),
          upperTrigram,
          lowerTrigram,
          movingYaoPosition: movingYao
        };

        logger.info('Manual divination completed', { result });
        set({ currentResult: result });
      },

      performTimeDivination: () => {
        logger.info('Performing time-based divination');

        const now = new Date();
        const lunar = Lunar.fromDate(now);

        // 地支对应数字（用于年份和时辰）
        const earthlyBranchesMap: Record<string, number> = {
          '子': 1, '丑': 2, '寅': 3, '卯': 4, '辰': 5, '巳': 6,
          '午': 7, '未': 8, '申': 9, '酉': 10, '戌': 11, '亥': 12
        };

        // 农历月份地支对应数字（寅月=1，卯月=2，...）
        const lunarMonthMap: Record<string, number> = {
          '寅': 1, '卯': 2, '辰': 3, '巳': 4, '午': 5, '未': 6,
          '申': 7, '酉': 8, '戌': 9, '亥': 10, '子': 11, '丑': 12
        };

        // 从干支中提取地支（取第二个字符）
        const getEarthlyBranch = (ganZhi: string) => ganZhi.charAt(1);

        // 年份：取地支数字
        const yearGanZhi = lunar.getYearInGanZhi();
        const yearBranch = getEarthlyBranch(yearGanZhi);
        const yearValue = earthlyBranchesMap[yearBranch];

        // 月份：取地支数字（寅月=1）
        const monthGanZhi = lunar.getMonthInGanZhi();
        const monthBranch = getEarthlyBranch(monthGanZhi);
        const monthValue = lunarMonthMap[monthBranch];

        // 日期：直接取农历日
        const dayValue = lunar.getDay();

        // 时辰：取地支数字
        const earthlyBranches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
        const hourBranchIndex = Math.floor((now.getHours() + 1) / 2) % 12;
        const hourBranch = earthlyBranches[hourBranchIndex];
        const hourValue = earthlyBranchesMap[hourBranch];

        logger.info('Calculated time values', {
          yearGanZhi, yearValue,
          monthGanZhi, monthValue,
          dayValue,
          hourBranch, hourValue
        });

        const trigrams: Trigram[] = ['乾', '兑', '离', '震', '巽', '坎', '艮', '坤'];

        const upperTrigram = trigrams[(yearValue + monthValue + dayValue - 1) % 8];
        const lowerTrigram = trigrams[(yearValue + monthValue + dayValue + hourValue - 1) % 8];

        const movingYaoIndex = (yearValue + monthValue + dayValue + hourValue - 1) % 6;
        const movingYao = ['初爻', '二爻', '三爻', '四爻', '五爻', '上爻'][movingYaoIndex] as YaoPosition;

        logger.info('Time divination parameters calculated', { upperTrigram, lowerTrigram, movingYao });

        set({ upperTrigram, lowerTrigram, movingYao });
        get().performManualDivination(upperTrigram, lowerTrigram, movingYao);
      },

      saveCurrentResult: async () => {
        const { currentResult, savedResults } = get();
        logger.info('Attempting to save divination result', { currentResult });
        
        if (currentResult) {
          const exists = savedResults.some(
            result => result.timestamp === currentResult.timestamp
          );
          
          if (!exists) {
            logger.info('Saving new divination result');
            set({ savedResults: [...savedResults, currentResult] });
            
            // Asynchronously save to server
            asyncQueue.enqueue(
              `save-${currentResult.timestamp}`,
              async () => await divinationApi.saveResult(currentResult)
            );
            
            return true;
          } else {
            logger.warning('Divination result already exists', { timestamp: currentResult.timestamp });
          }
        } else {
          logger.warning('No current result to save');
        }
        return false;
      },

      loadSavedResult: (result: HexagramResult) => {
        logger.info('Loading saved divination result', { timestamp: result.timestamp });
        set({ 
          currentResult: result,
          upperTrigram: result.upperTrigram,
          lowerTrigram: result.lowerTrigram,
          movingYao: result.movingYaoPosition
        });
      },

      deleteSavedResult: async (timestamp: string) => {
        logger.info('Deleting saved divination result', { timestamp });
        const { savedResults } = get();
        
        set({
          savedResults: savedResults.filter(result => result.timestamp !== timestamp)
        });

        // Asynchronously delete from server
        asyncQueue.enqueue(
          `delete-${timestamp}`,
          async () => await divinationApi.deleteResult(timestamp)
        );
      },

      syncWithServer: async () => {
        logger.info('Syncing divination results with server');
        try {
          const serverResults = await divinationApi.getResults();
          set({ savedResults: serverResults });
          logger.info('Divination results synced successfully', { count: serverResults.length });
        } catch (error) {
          logger.error('Failed to sync divination results', error);
        }
      },

      reset: () => {
        logger.info('Resetting divination store');
        set({
          currentResult: null,
          savedResults: [],
          upperTrigram: '',
          lowerTrigram: '',
          movingYao: ''
        });
      }
    }),
    {
      name: 'divination-storage',
      partialize: (state) => ({ 
        savedResults: state.savedResults 
      }),
    }
  )
);